import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './FilterablePoolSelect.scss';

/**
 * Filterable pool select component - used when there can be hundreds of pools.
 * Contains a search field and displays filtered results in a dropdown.
 */
export default function FilterablePoolSelect({
  pools = [],
  value,
  onChange,
  placeholder,
  emptyOptionLabel,
  disabled = false,
}) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('filter_pool_default_ph');
  const resolvedEmptyLabel = emptyOptionLabel ?? t('pump_modal_pool_empty');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Find the selected pool for displaying the name
  const selectedPool = useMemo(() => {
    if (!value) return null;
    return pools.find(p => p.id === parseInt(value) || p.id === value);
  }, [pools, value]);

  // Suodata altaat hakutermin perusteella
  const filteredPools = useMemo(() => {
    if (!searchTerm.trim()) return pools;
    const term = searchTerm.toLowerCase();
    return pools.filter(pool => 
      pool.name?.toLowerCase().includes(term)
    );
  }, [pools, searchTerm]);

  // Sulje dropdown kun klikataan ulkopuolella
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.pool-option');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setHighlightedIndex(-1);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSelectPool = (poolId) => {
    onChange(poolId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    const totalOptions = filteredPools.length + 1; // +1 for empty option

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : totalOptions - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === 0) {
          handleSelectPool('');
        } else if (highlightedIndex > 0 && filteredPools[highlightedIndex - 1]) {
          handleSelectPool(filteredPools[highlightedIndex - 1].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const displayValue = isOpen 
    ? searchTerm 
    : (selectedPool ? selectedPool.name : '');

  return (
    <div 
      className={`filterable-pool-select ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
      ref={containerRef}
    >
      <div className="select-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="form-input pool-search-input"
          value={displayValue}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          autoComplete="off"
        />
        <span className="select-arrow" onClick={() => !disabled && setIsOpen(!isOpen)}>
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path fill="#333" d="M6 9L1 4h10z" />
          </svg>
        </span>
        {selectedPool && !isOpen && (
          <button 
            type="button"
            className="clear-button"
            onClick={(e) => {
              e.stopPropagation();
              handleSelectPool('');
            }}
            disabled={disabled}
            aria-label={t('filter_pool_clear_aria')}
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="pool-dropdown-menu" ref={listRef}>
          {/* "Ei sijaintia" -vaihtoehto */}
          <div
            className={`pool-option pool-option-empty ${highlightedIndex === 0 ? 'highlighted' : ''} ${!value ? 'selected' : ''}`}
            onClick={() => handleSelectPool('')}
          >
            {resolvedEmptyLabel}
          </div>

          {filteredPools.length === 0 ? (
            <div className="pool-option-no-results">
              {t('filter_pool_no_results', { term: searchTerm })}
            </div>
          ) : (
            filteredPools.map((pool, index) => (
              <div
                key={pool.id}
                className={`pool-option ${highlightedIndex === index + 1 ? 'highlighted' : ''} ${value === pool.id || value === String(pool.id) ? 'selected' : ''}`}
                onClick={() => handleSelectPool(pool.id)}
              >
                {pool.name}
              </div>
            ))
          )}

          {pools.length > 10 && (
            <div className="pool-count-info">
              {t('filter_pool_count', { shown: filteredPools.length, total: pools.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

